import { createFileRoute } from '@tanstack/react-router';
import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@ouropix/ui';
import { Button } from '@ouropix/ui';
import { Badge } from '@ouropix/ui';
import { Alert, AlertDescription } from '@ouropix/ui';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@ouropix/ui';
import { ConfirmModal } from '@ouropix/ui';
import { Skeleton } from '@ouropix/ui';
import {
  Monitor, Smartphone, Tablet, Laptop, ShieldAlert, ShieldX, Loader2,
  MapPin, Clock, RefreshCw, Info,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { apiErrorMessage } from '@/lib/api-helpers';
import { api } from '@/lib/api';
import { parseUA, countryFlag } from '@/lib/ua-parser';
import { useAuthStore } from '@/stores/auth';

export const Route = createFileRoute('/_admin/seguranca/sessoes')({
  component: SessoesPage,
});

interface AdminSessionRow {
  id: string;
  device_fingerprint?: string | null;
  ip_address: string | null;
  ip_country?: string | null;
  ip_country_code?: string | null;
  ip_city?: string | null;
  user_agent: string | null;
  last_used_at: string | null;
  created_at: string | null;
  is_current?: boolean;
}

function deviceIcon(device: 'desktop' | 'mobile' | 'tablet' | 'unknown') {
  switch (device) {
    case 'mobile':
      return Smartphone;
    case 'tablet':
      return Tablet;
    case 'desktop':
      return Laptop;
    default:
      return Monitor;
  }
}

function SessoesPage() {
  const qc = useQueryClient();
  const session = useAuthStore((s) => s.session);
  const [revokeTarget, setRevokeTarget] = useState<AdminSessionRow | null>(null);
  const [revokeAllOpen, setRevokeAllOpen] = useState(false);

  const { data, isLoading, isError, refetch, error } = useQuery<AdminSessionRow[]>({
    queryKey: ['admin-sessions'],
    queryFn: async () => {
      const { data } = await api.get('/auth/admin/sessions');
      const rows: AdminSessionRow[] = Array.isArray(data)
        ? data
        : (data?.sessions ?? data?.data ?? []);
      return rows;
    },
    retry: false,
  });

  const sessions = data ?? [];
  const hasOthers = sessions.some((s) => !s.is_current);

  const revokeOne = useMutation({
    mutationFn: (id: string) => api.delete(`/auth/admin/sessions/${id}`),
    onSuccess: () => {
      toast.success('Sessão revogada');
      qc.invalidateQueries({ queryKey: ['admin-sessions'] });
      setRevokeTarget(null);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Falha ao revogar sessão')),
  });

  const revokeAll = useMutation({
    mutationFn: () => api.post('/auth/admin/sessions/revoke-all-others', {}),
    onSuccess: (res: any) => {
      const count = res?.data?.revoked_count ?? res?.data?.revokedCount ?? 0;
      toast.success(count > 0
        ? `${count} ${count === 1 ? 'sessão revogada' : 'sessões revogadas'}`
        : 'Nenhuma sessão para revogar');
      qc.invalidateQueries({ queryKey: ['admin-sessions'] });
      setRevokeAllOpen(false);
    },
    onError: (err) => toast.error(apiErrorMessage(err, 'Falha ao revogar sessões')),
  });

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShieldAlert className="h-7 w-7" />
            Sessões ativas
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            Gerencie dispositivos conectados à sua conta. Revogue qualquer sessão que você não reconheça.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => refetch()} disabled={isLoading}>
            <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
            Atualizar
          </Button>
          <Button
            variant="destructive"
            size="sm"
            disabled={!hasOthers || revokeAll.isPending}
            onClick={() => setRevokeAllOpen(true)}
          >
            <ShieldX className="h-4 w-4 mr-2" />
            Revogar todas outras
          </Button>
        </div>
      </div>

      {isError && (
        <Alert variant="destructive">
          <AlertDescription>
            Não foi possível carregar sessões:{' '}
            {(error as any)?.response?.status === 404
              ? 'endpoint ainda não disponível no gateway.'
              : (error as Error)?.message ?? 'erro desconhecido'}
          </AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Monitor className="h-4 w-4" />
            Dispositivos conectados
          </CardTitle>
          <CardDescription>
            {session?.email && (
              <>Conta: <strong>{session.email}</strong></>
            )}
          </CardDescription>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <SessionsTableSkeleton />
          ) : sessions.length === 0 && !isError ? (
            <div className="flex flex-col items-center gap-2 py-10 text-sm text-muted-foreground">
              <Info className="h-6 w-6" />
              Nenhuma sessão ativa encontrada.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Dispositivo</TableHead>
                    <TableHead>Localização</TableHead>
                    <TableHead>Último acesso</TableHead>
                    <TableHead>Criada</TableHead>
                    <TableHead className="w-32">Status</TableHead>
                    <TableHead className="w-24 text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sessions.map((s) => {
                    const ua = parseUA(s.user_agent);
                    const Icon = deviceIcon(ua.device);
                    return (
                      <TableRow key={s.id} className={s.is_current ? 'bg-primary/5' : ''}>
                        <TableCell>
                          <div className="flex items-center gap-3">
                            <div className="p-1.5 rounded-md bg-muted">
                              <Icon className="h-4 w-4" />
                            </div>
                            <div>
                              <div className="font-medium text-sm">{ua.browser}</div>
                              <div className="text-xs text-muted-foreground">{ua.os}</div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{countryFlag(s.ip_country_code)}</span>
                            <div className="text-sm">
                              <div className="font-mono text-xs">{s.ip_address ?? '-'}</div>
                              <div className="text-xs text-muted-foreground flex items-center gap-1">
                                <MapPin className="h-3 w-3" />
                                {[s.ip_city, s.ip_country].filter(Boolean).join(', ') || 'Desconhecido'}
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          <div className="flex items-center gap-1.5">
                            <Clock className="h-3 w-3" />
                            {s.last_used_at
                              ? formatDistanceToNow(new Date(s.last_used_at), { addSuffix: true, locale: ptBR })
                              : '-'}
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground">
                          {s.created_at
                            ? formatDistanceToNow(new Date(s.created_at), { addSuffix: true, locale: ptBR })
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {s.is_current ? (
                            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
                              Esta sessão
                            </Badge>
                          ) : (
                            <Badge variant="outline">Ativa</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          {!s.is_current && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setRevokeTarget(s)}
                              disabled={revokeOne.isPending}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                            >
                              {revokeOne.isPending && revokeOne.variables === s.id ? (
                                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                              ) : (
                                'Revogar'
                              )}
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <ConfirmModal
        isOpen={!!revokeTarget}
        onClose={() => setRevokeTarget(null)}
        title="Revogar sessão?"
        description={
          revokeTarget
            ? `Esta ação encerra o acesso desta sessão (${parseUA(revokeTarget.user_agent).browser} · ${revokeTarget.ip_address ?? '-'}). Não pode ser desfeita.`
            : ''
        }
        confirmText="Revogar sessão"
        variant="destructive"
        isLoading={revokeOne.isPending}
        onConfirm={() => {
          if (revokeTarget) revokeOne.mutate(revokeTarget.id);
        }}
      />
      <ConfirmModal
        isOpen={revokeAllOpen}
        onClose={() => setRevokeAllOpen(false)}
        title="Revogar todas as outras sessões?"
        description="Você permanecerá logado neste dispositivo. Todos os outros dispositivos serão deslogados."
        confirmText="Revogar outras"
        variant="destructive"
        isLoading={revokeAll.isPending}
        onConfirm={() => revokeAll.mutate()}
      />
    </div>
  );
}

function SessionsTableSkeleton() {
  return (
    <div className="space-y-2">
      {[0, 1, 2].map((i) => (
        <div key={i} className="flex items-center gap-4 py-2">
          <Skeleton className="h-8 w-8 rounded-md" />
          <Skeleton className="h-4 flex-1" />
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-6 w-20" />
        </div>
      ))}
    </div>
  );
}
